<template>
  <section class="dm-requests" :aria-busy="loading ? 'true' : 'false'">
    <header><button type="button" :aria-label="$t('directMessage.back')" @click="$emit('back')"><i class="el-icon-back" /></button><div><strong>{{ $t("directMessage.requests") }}</strong><small>{{ $t("directMessage.requestsTip") }}</small></div></header>
    <main>
      <section v-if="loading && !items.length" class="dm-state"><i class="el-icon-loading" />{{ $t("directMessage.loadingRequests") }}</section>
      <article v-for="item in items" :key="item.id">
        <platform-avatar :src="item.avatar_url" :alt="item.name" />
        <div><strong>{{ item.name }}</strong><p>{{ item.content }}</p><small v-if="Number(item.message_count) > 1">{{ $t("directMessage.requestMessageCount", { count: Number(item.message_count) }) }}</small><time>{{ $d(item.message_time || item.create_time, { dateStyle: "short", timeStyle: "short" }) }}</time></div>
        <aside><button type="button" :disabled="workingId === item.id" @click="accept(item)"><i v-if="workingId === item.id" class="el-icon-loading" />{{ $t("directMessage.accept") }}</button><button type="button" :disabled="workingId === item.id" @click="ignore(item)">{{ $t("directMessage.ignore") }}</button><button class="danger" type="button" :disabled="workingId === item.id" @click="reportBlock(item)">{{ $t("directMessage.reportBlock") }}</button></aside>
      </article>
      <section v-if="!loading && !items.length" class="dm-state"><i class="el-icon-message" /><strong>{{ $t("directMessage.noRequests") }}</strong><p>{{ $t("directMessage.noRequestsTip") }}</p></section>
      <button v-if="pagination.has_more" class="dm-more" :disabled="loading" @click="load(false)">{{ $t("notificationUi.viewMore") }}</button>
    </main>
  </section>
</template>

<script>
import { acceptDirectMessageRequest, getDirectMessageRequests, ignoreDirectMessageRequest, reportBlockDirectMessageRequest } from "../api/directMessage"

export default {
  name: "DirectMessageRequests",
  data() { return { items: [], pagination: { page: 1, has_more: false }, loading: false, workingId: 0 } },
  created() { this.load(true) },
  methods: {
    async load(reset) {
      if (this.loading) return
      this.loading = true
      try {
        const page = reset ? 1 : Number(this.pagination.page || 1) + 1
        const res = await getDirectMessageRequests({ page, limit: 20 })
        this.items = reset ? (res.data.list || []) : this.items.concat(res.data.list || [])
        this.pagination = res.data.pagination || { page, has_more: false }
        this.$emit("count-change", Number(this.pagination.total || 0))
      } finally { this.loading = false }
    },
    async accept(item) {
      if (this.workingId) return
      this.workingId = item.id
      try {
        await acceptDirectMessageRequest({ request_id: item.id })
        this.items = this.items.filter(row => row.id !== item.id)
        this.$emit("count-change", this.items.length)
        this.$emit("accepted", { target_type: "user", target_id: item.sender_user_id })
      } finally { this.workingId = 0 }
    },
    async ignore(item) {
      if (this.workingId) return
      try {
        await this.$notice.confirm(this.$t("directMessage.ignoreConfirm"), this.$t("directMessage.ignore"), { type: "warning" })
      } catch (_) { return }
      this.workingId = item.id
      try {
        await ignoreDirectMessageRequest({ request_id: item.id })
        this.items = this.items.filter(row => row.id !== item.id)
        this.$emit("count-change", this.items.length)
      } finally { this.workingId = 0 }
    },
    async reportBlock(item) {
      if (this.workingId) return
      try {
        await this.$notice.confirm(this.$t("directMessage.reportBlockConfirm"), this.$t("directMessage.reportBlock"), { type: "warning" })
      } catch (_) { return }
      this.workingId = item.id
      try {
        await reportBlockDirectMessageRequest({ request_id: item.id, reason_code: "spam" })
        this.items = this.items.filter(row => row.id !== item.id)
        this.$emit("count-change", this.items.length)
        this.$notice.success(this.$t("directMessage.reportBlockDone"))
      } finally { this.workingId = 0 }
    }
  }
}
</script>

<style scoped>
.dm-requests{position:absolute;z-index:8;inset:0;display:flex;flex-direction:column;background:radial-gradient(circle at 90% 0,rgba(255,49,88,.12),transparent 34%),#0d0e12;color:#fff}.dm-requests>header{min-height:72px;display:flex;align-items:center;gap:12px;padding:10px 16px;border-bottom:1px solid rgba(255,255,255,.08)}header>button{width:38px;height:38px;border:1px solid #343640;border-radius:50%;background:#1b1d24;color:#fff}header div{min-width:0}header strong,header small{display:block}header small{margin-top:4px;color:#858892;font-size:10px;overflow-wrap:anywhere}.dm-requests main{flex:1;overflow-y:auto;padding:8px 16px 28px;scrollbar-width:none}.dm-requests main::-webkit-scrollbar{display:none}article{display:grid;grid-template-columns:48px minmax(0,1fr) auto;gap:11px;align-items:center;padding:14px 0;border-bottom:1px solid rgba(255,255,255,.07)}article>img{width:48px;height:48px;border-radius:50%;object-fit:cover}article div{min-width:0}article strong{font-size:13px}article p{margin:5px 0;color:#bdc0c8;font-size:11px;overflow-wrap:anywhere}article div>small{display:block;margin:3px 0;color:#ff7185;font-size:8px}article time{display:block;color:#666a74;font-size:8px}article aside{display:flex;flex-direction:column;gap:6px}article aside button{min-width:68px;padding:7px 10px;border:1px solid #363945;border-radius:14px;background:#1b1d24;color:#ddd;font-size:10px}article aside button:first-child{border-color:transparent;background:#ff455d;color:#fff}.dm-state{min-height:280px;display:grid;place-content:center;justify-items:center;gap:8px;color:#7c7f89;text-align:center}.dm-state i{font-size:38px}.dm-state strong{color:#fff}.dm-state p{max-width:270px;margin:0;font-size:11px;line-height:1.6}.dm-more{display:block;margin:16px auto;padding:8px 16px;border:1px solid #343640;border-radius:18px;background:#1b1d24;color:#bbb}@media(max-width:350px){article{grid-template-columns:42px minmax(0,1fr)}article>img{width:42px;height:42px}article aside{grid-column:2;flex-direction:row}}
</style>
